
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeMount,
  getCurrentInstance,
} from 'vue';
import { Method } from './method';
import LegendComponent from './Legend.vue';
import Parse from './Parse.vue';
import StatusBadge from '@/components/ui/StatusBadge.vue';
import PlatformSpecification from './PlatformSpecification.vue';
import GlobalBus from '@/plugins/GlobalBus';
import { GlobalBusNames, StoreMutations } from '@/templates/DataNames';
import { convertToHtmlHelper, isEmptyObject, orderObject } from '@/heplers';
import { MethodType } from '@/templates/NewRoutes';
import CodeExampleBody from '@/components/docs-main/code-rails-view/CodeExampleBody.vue';
import CodeBlockAnchor from '@/components/docs-main/code-rails-view/CodeBlockAnchor.vue';
import { BadgesType } from '@/templates/References';
import { ArticleHeader, type ArticleHeaderProps } from '@/components/docs-main/article-header';

interface StoreProxy {
  getters: Record<string, unknown>;
  state: Record<string, unknown>;
  commit: (mutation: string, payload?: unknown) => void;
}

export default defineComponent({
  name: 'TutorialFolder',
  components: {
    ArticleHeader,
    CodeBlockAnchor,
    LegendComponent,
    Parse,
    StatusBadge,
    PlatformSpecification,
    Method,
    CodeExampleBody,
  },
  setup() {
    const instance = getCurrentInstance();
    const store = computed<StoreProxy | undefined>(
      () => instance?.proxy?.$store as StoreProxy | undefined
    );
    const route = computed(() => instance?.proxy?.$route);

    const content = ref<HTMLElement | null>(null);

    const data = computed<MethodType>(
      () => (store.value?.getters?.getContent as MethodType) ?? ({} as MethodType)
    );

    const pageRendered = computed<boolean>(
      () => (store.value?.state?.pageRendered as boolean) ?? false
    );

    const getDescription = computed((): string => {
      return data.value?.description
        ? data.value.description
        : 'Build smart IVRs, launch automated call campaigns and make trigger outbound calls with our visual flow builder to improve customer experience in no time.';
    });

    /**
     * Собираем объект для alert блоков
     */
    const statusBadges = computed(() => {
      if (!data.value?.attributes) return {};

      const allowedBadgeNames: BadgesType[] = Object.values(BadgesType);

      const filter = (
        attributes: Record<string, unknown>,
        allowed: BadgesType[]
      ): Record<string, string> =>
        allowed.reduce<Record<string, string>>((acc, name) => {
          if (attributes[name] && typeof attributes[name] === 'string') {
            acc[name] = attributes[name] as string;
          }
          return acc;
        }, {});

      return filter(
        (data.value.attributes as unknown) as Record<string, unknown>,
        allowedBadgeNames
      );
    });

    const orderedData = computed(() => orderObject(data.value));

    const isReferences = computed(
      () => data.value?.fqdn && data.value.fqdn.split('.')[0] === 'references'
    );

    const isTypedef = computed(() => {
      const kind = data.value?.kind as string | undefined;
      const fqdnParts = (route.value?.meta?.fqdn as string)?.split('.') ?? [];
      return (
        (kind && ['typedef', 'function'].includes(kind)) ||
        (fqdnParts[1] === 'unitysdk' && kind === 'events')
      );
    });

    const dataBadges = computed(() => {
      return Object.keys(data.value?.attributes || {}).filter(Boolean);
    });

    const articleHeaderData = computed<ArticleHeaderProps>(() => {
      const { title, kind, attributes } = data.value;
      return {
        title,
        attributes: dataBadges.value,
        kind: String(kind),
        version: attributes?.since
      };
    })

    const setListeners = (listenersState: boolean) => {
      store.value?.commit(StoreMutations.SET_LISTENERS_STATE, listenersState);
    };

    const checkTargetLink = (link: string) => {
      return link.includes('http') ? '_blank' : '_self';
    };

    /** From Tutorial.vue **/
    const showCodeExample = (obj: { kind?: string; title?: string }) => {
      return obj.kind === 'content_source' && obj.title;
    };

    const convertToHtml = (mdString: string) => {
      if (!mdString) return '';
      return convertToHtmlHelper(mdString);
    };

    // Подключаемся к слушателям
    const bindEvents = () => {
      GlobalBus.on(GlobalBusNames.NEWLANGUAGEPACKLOADED, findAllLanguages);
    };

    const findAllLanguages = () => {
      const resultObj: string[] = [];
      const children = data.value?.children;
      if (!children || !children.length) return;

      let firstChild;

      firstChild = children[0] as { title?: string | Record<string, string> };

      if (firstChild.title && typeof firstChild.title === 'object') {
        Object.keys(firstChild.title).forEach((lang: string) => resultObj.push(lang));
      }
      if (resultObj.length > 0) {
        GlobalBus.emit(GlobalBusNames.CHECKENABLEDLANGS, resultObj);
      }
    };

    const initHandlers = () => {
      bindEvents();

      if (isEmptyObject(orderedData.value)) return;
    };

    onBeforeMount(() => {
      GlobalBus.off(GlobalBusNames.NEWLANGUAGEPACKLOADED);
      GlobalBus.off('anchorScroll');
    });

    onMounted(() => {
      // Prism refers to a UMD global
      if (
        content.value &&
        typeof ((window as unknown) as { Prism?: { highlightAllUnder: (el: HTMLElement) => void } })
          .Prism !== 'undefined'
      ) {
        ((window as unknown) as {
          Prism: { highlightAllUnder: (el: HTMLElement) => void };
        }).Prism.highlightAllUnder(content.value);
      }
      setListeners(false);
      bindEvents();
      setTimeout(() => {
        setListeners(true);
      }, 300);
    });

    watch(pageRendered, (val: boolean) => {
      if (val) {
        initHandlers();
      }
    });

    return {
      content,
      data,
      getDescription,
      statusBadges,
      orderedData,
      isReferences,
      isTypedef,
      dataBadges,
      articleHeaderData,
      checkTargetLink,
      convertToHtml,
      showCodeExample,
    };
  },
});
